import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import { getGlobalVoiceNumbers, createGlobalVoiceNumber, updateGlobalVoiceNumber, deleteGlobalVoiceNumber, toggleGlobalVoiceNumberStatus } from '../../../api/backendApi';

function E164Input({ value, onChange, error }) {
  return (
    <TextField
      fullWidth
      label="E.164"
      placeholder="+1XXXXXXXXXX"
      value={value}
      onChange={e => onChange(e.target.value)}
      error={!!error}
      helperText={error || ''}
    />
  );
}

function AddEditDialog({ open, onClose, onSubmit, initial }) {
  const [e164, setE164] = useState(initial?.e164 || '');
  const [region, setRegion] = useState(initial?.region || '');
  const [rotation, setRotation] = useState(initial?.rotation || 'roundrobin');
  const [status, setStatus] = useState(initial?.status || 'active');
  const [err, setErr] = useState('');

  useEffect(() => {
    setE164(initial?.e164 || '');
    setRegion(initial?.region || '');
    setRotation(initial?.rotation || 'roundrobin');
    setStatus(initial?.status || 'active');
    setErr('');
  }, [initial, open]);

  const handleSubmit = async () => {
    const isValid = typeof e164 === 'string' && /^\+[0-9]{10,15}$/.test(e164.trim());
    if (!isValid) {
      setErr('Must be E.164 like +14165551234');
      return;
    }
    const payload = { e164: e164.trim(), region: region || undefined, rotation, status };
    await onSubmit(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{initial?.id ? 'Edit Pool Number' : 'Add Pool Number'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <E164Input value={e164} onChange={setE164} error={err} />
          <TextField label="Region" value={region} onChange={e => setRegion(e.target.value)} fullWidth />
          <TextField label="Rotation" value={rotation} onChange={e => setRotation(e.target.value)} fullWidth />
          <TextField label="Status" value={status} onChange={e => setStatus(e.target.value)} fullWidth />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>{initial?.id ? 'Save' : 'Add'}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function GlobalPstnPoolTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dlgOpen, setDlgOpen] = useState(false);
  const [dlgInitial, setDlgInitial] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await getGlobalVoiceNumbers();
      const list = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
      setRows(list);
    } catch (e) {
      setError(String(e?.message || 'Failed to load global pool'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const onAdd = () => { setDlgInitial(null); setDlgOpen(true); };
  const onEdit = (row) => { setDlgInitial(row); setDlgOpen(true); };

  const onSubmit = async (payload) => {
    setBusy(true);
    try {
      if (dlgInitial?.id) await updateGlobalVoiceNumber(dlgInitial.id, payload);
      else await createGlobalVoiceNumber(payload);
      setDlgOpen(false);
      await refresh();
    } catch (e) {
      setError(String(e?.message || 'Failed to save'));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (row) => {
    const ok = window.confirm('Delete this pool number?');
    if (!ok) return;
    setBusy(true);
    try {
      await deleteGlobalVoiceNumber(row.id);
      await refresh();
    } catch (e) {
      setError(String(e?.message || 'Failed to delete'));
    } finally {
      setBusy(false);
    }
  };

  const onToggleStatus = async (row) => {
    setBusy(true);
    try {
      const next = (row.status || 'active') === 'active' ? 'suspended' : 'active';
      await toggleGlobalVoiceNumberStatus(row.id, next);
      await refresh();
    } catch (e) {
      setError(String(e?.message || 'Failed to update status'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Global PSTN Pool</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={onAdd}>Add Number</Button>
      </Stack>
      {error && <Box sx={{ color: 'error.main', mb: 2 }}>{error}</Box>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
      ) : rows.length === 0 ? (
        <Box sx={{ color: 'text.secondary', fontSize: 14 }}>No pool numbers yet.</Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>E.164</TableCell>
              <TableCell>Region</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Rotation</TableCell>
              <TableCell>Last Used</TableCell>
              <TableCell>Daily Count</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.id} hover>
                <TableCell>{r.e164}</TableCell>
                <TableCell>{r.region || '—'}</TableCell>
                <TableCell>
                  <Chip size="small" label={r.status || 'active'} color={(r.status || 'active') === 'active' ? 'success' : 'default'} />
                </TableCell>
                <TableCell>{r.rotation || '—'}</TableCell>
                <TableCell>{r?.usage?.lastUsedAt ? new Date(r.usage.lastUsedAt).toLocaleString() : '—'}</TableCell>
                <TableCell>{r?.usage?.dailyCount ?? 0}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Tooltip title="Edit"><span><IconButton size="small" onClick={() => onEdit(r)}><Edit fontSize="small" /></IconButton></span></Tooltip>
                    <Tooltip title={(r.status || 'active') === 'active' ? 'Suspend' : 'Activate'}><span><Button size="small" variant="outlined" onClick={() => onToggleStatus(r)} disabled={busy}>{(r.status || 'active') === 'active' ? 'Suspend' : 'Activate'}</Button></span></Tooltip>
                    <Tooltip title="Delete"><span><IconButton size="small" onClick={() => onDelete(r)} disabled={busy}><Delete fontSize="small" /></IconButton></span></Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <AddEditDialog open={dlgOpen} onClose={() => setDlgOpen(false)} onSubmit={onSubmit} initial={dlgInitial} />
    </Box>
  );
}
